import type { DashboardWeatherOrder } from '@/api/data-contracts';
import type { NightSection, TaskWeatherData, WeatherSeverity } from '@/features/service-tasks';

export interface OrderForecastDay {
  date: string;
  section: NightSection | null;
  reasonNotCalculated: TaskWeatherData['reasonNotCalculated'];
  forecastAvailableFrom: string | null;
}

export interface OrderForecast {
  orderId: string;
  orderNumber: string;
  brand: string;
  location: string;
  city: string;
  risk: WeatherSeverity | null;
  days: OrderForecastDay[];
}

const RISK_RANK: Record<WeatherSeverity, number> = { BLUE: 1, ORANGE: 2, RED: 3 };

const rankOf = (risk: WeatherSeverity | null): number => (risk ? RISK_RANK[risk] : 0);

const firstDate = (forecast: OrderForecast): string => forecast.days[0]?.date ?? '';

const worstOf = (candidates: TaskWeatherData[]): TaskWeatherData | undefined =>
  candidates.reduce<TaskWeatherData | undefined>(
    (worst, current) => (!worst || rankOf(current.badge) > rankOf(worst.badge) ? current : worst),
    undefined,
  );

export const buildOrderForecasts = (
  orders: DashboardWeatherOrder[],
  weather: Map<string, TaskWeatherData>,
): OrderForecast[] =>
  orders
    .map((order) => {
      const days = order.days.map((day) => {
        const results = day.taskIds
          .map((id) => weather.get(id))
          .filter((w): w is TaskWeatherData => w !== undefined);
        const worst = worstOf(results.filter((w) => w.todaySection !== null));
        return {
          date: day.date,
          section: worst?.todaySection ?? null,
          reasonNotCalculated: worst ? null : (results[0]?.reasonNotCalculated ?? null),
          forecastAvailableFrom: day.forecastAvailableFrom ?? null,
          risk: worst?.badge ?? null,
        };
      });
      const risk = days.reduce<WeatherSeverity | null>(
        (max, day) => (rankOf(day.risk) > rankOf(max) ? day.risk : max),
        null,
      );
      return {
        orderId: order.orderId,
        orderNumber: order.orderNumber,
        brand: order.brand,
        location: order.location,
        city: order.city,
        risk,
        days: days.map(({ risk: _risk, ...day }) => day),
      };
    })
    .sort(
      (a, b) =>
        rankOf(b.risk) - rankOf(a.risk) ||
        firstDate(a).localeCompare(firstDate(b)) ||
        a.orderNumber.localeCompare(b.orderNumber),
    );

export const weatherTaskIds = (orders: DashboardWeatherOrder[]): string[] =>
  orders.flatMap((order) => order.days.flatMap((day) => day.taskIds));
